import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorExpenses.css';
import { formatCurrency } from '../utils/formatters';
import CreateExpensePage from './CreateExpensePage';
import ExpenseImportModal from './ExpenseImportModal';
import HomeIcon from './HomeIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = '/api/expenses';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Custom SVGs for a polished interface ── */
const Icon = ({ name, size = 16, strokeWidth = 1.75 }) => {
    const paths = {
        plus: ['M12 5v14', 'M5 12h14'],
        search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
        edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
        trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
        eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
        cog: ['M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
        template: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
        import: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
        filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
        refresh: ['M21.5 2v6h-6', 'M21.34 15.57a10 10 0 1 1-.57-8.38'],
        excel: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M8 13h8', 'M8 17h8'],
        printer: ['M6 9V2h12v7', 'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2', 'M6 14h12v8H6z'],
        pdf: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
        arrowDown: ['M12 5v14', 'M19 12l-7 7-7-7'],
        percent: ['M19 5L5 19', 'M6.5 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M17.5 19.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
        wallet: ['M20 12V8H6a2 2 0 0 1 2-2h12V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3', 'M20 12h-4a1 1 0 0 0 0 2h4v-2z'],
        grid: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z']
    };

    const d = paths[name] || [];
    return (
        <svg width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            {d.map((p, i) => <path key={i} d={p} />)}
        </svg>
    );
};

export default function OperatorExpenses({ user: propUser, operatorDetails }) {
    const [expenses, setExpenses] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [clients, setClients] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('All');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    const [loading, setLoading] = useState(true);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    useEffect(() => {
        fetchExpenses();
        fetchMasterData();
    }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/${operatorId}`);
            setExpenses(res.data.expenses);
            setTotalAmount(res.data.totalAmount);
        } catch (err) {
            console.error('Fetch expenses error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async () => {
        try {
            const [masterRes, vendorRes, vehicleRes, deptRes, driverRes, clientRes] = await Promise.all([
                axios.get(MASTER_DATA_API),
                axios.get(`/api/vendors/${operatorId}`),
                axios.get(`/api/vehicles/${operatorId}`),
                axios.get(`/api/departments/${operatorId}`),
                axios.get(`/api/drivers/${operatorId}`),
                axios.get(`/api/clients/${operatorId}`),
            ]);
            const grouped = masterRes.data.reduce((acc, item) => {
                const cat = item.category;
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
            }, {});
            setCategories(grouped['expense-categories'] || []);
            setPaymentMethods(grouped['payment-methods'] || []);
            setVendors(vendorRes.data);
            setVehicles(vehicleRes.data);
            setDepartments(deptRes.data);
            setDrivers(driverRes.data);
            setClients(clientRes.data);
        } catch (err) {
            console.error('Fetch master data error:', err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchExpenses();
        } catch (err) {
            console.error('Delete expense error:', err);
        }
    };

    const openCreate = (item = null) => {
        setEditItem(item);
        setCreatePageOpen(true);
    };

    const handleManageCategories = () => {
        window.location.hash = 'expense-categories';
    };

    const handleDownloadTemplate = () => {
        const csv = "Date,Category,PaymentMethod,Amount,VehiclePlate,VendorName,Notes\n2026-05-19,Fuel,Cash,150,FF 36716,Emarat,Fuel for coaster";
        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csv);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "expense_import_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedMonth('All');
        setFromDate('');
        setToDate('');
        setCurrentPage(1);
    };

    const handleExportExcel = () => {
        const headers = ["Date", "Category", "Plate No.", "Vendor", "Amount", "VAT", "Total", "Payment Method", "Account / Entity", "Paid By"];
        const rows = filtered.map(exp => {
            const dateStr = new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            const plateStr = exp.vehicleId?.plateNumber || '—';
            const vendorStr = exp.vendorId?.vendorName || '—';
            const isEx = exp.category?.toLowerCase().includes('fuel') || exp.category?.toLowerCase().includes('utility');
            const netAmt = isEx ? exp.amount : exp.amount * 0.95;
            const vatAmt = isEx ? 0 : exp.amount * 0.05;
            
            return [
                dateStr,
                exp.category,
                plateStr,
                vendorStr,
                netAmt.toFixed(2),
                isEx ? 'Exempt' : vatAmt.toFixed(2),
                exp.amount.toFixed(2),
                exp.paymentMethod || '—',
                exp.department ? `Dept: ${exp.department}` : exp.clientId?.companyName ? `Client: ${exp.clientId.companyName}` : 'General Petty Cash',
                exp.driverId?.name || '—'
            ];
        });

        let csvContent = "data:text/csv;charset=utf-8,"
            + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `expenses_report_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrintPDF = () => {
        window.print();
    };

    // Download the filtered expense records directly as a PDF file
    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();
        // Plain-text money (jsPDF fonts can't render symbols like د.ك / ₹)
        const money = (n) => {
            const cur = (formatCurrency(0, country) || '').replace(/[\d.,\s]/g, '').trim();
            const code = /^[A-Z]{3}$/.test(cur) ? cur + ' ' : '';
            return code + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        let totalAmt = 0, totalVat = 0, totalNet = 0;
        const body = filtered.map(exp => {
            const isEx = exp.category?.toLowerCase().includes('fuel') || exp.category?.toLowerCase().includes('utility');
            const netAmt = isEx ? exp.amount : exp.amount * 0.95;
            const vatAmt = isEx ? 0 : exp.amount * 0.05;
            totalAmt += Number(exp.amount || 0); totalVat += vatAmt; totalNet += netAmt;
            const account = exp.paymentMethod?.toLowerCase() === 'cash' ? 'Petty Cash'
                : (exp.paymentMethod?.toLowerCase() === 'bank' || exp.paymentMethod?.toLowerCase() === 'online') ? 'Bank Account'
                : 'General';
            return [
                new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                exp.category || '—',
                exp.vehicleId?.plateNumber || '—',
                exp.vendorId?.vendorName || '—',
                money(netAmt),
                isEx ? 'Exempt' : '5% VAT',
                money(exp.amount),
                exp.paymentMethod || '—',
                account,
                exp.driverId?.name || '—'
            ];
        });

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Business Expenses Report', 40, 40);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        const filterBits = [
            selectedCategory ? `Category: ${selectedCategory}` : null,
            selectedMonth && selectedMonth !== 'All' ? `Month: ${selectedMonth}` : null,
            fromDate ? `From: ${fromDate}` : null,
            toDate ? `To: ${toDate}` : null,
        ].filter(Boolean).join('   ');
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}${filterBits ? '   |   ' + filterBits : ''}`, 40, 56);
        doc.text(`Records: ${filtered.length}    Net: ${money(totalNet)}    VAT: ${money(totalVat)}    Total: ${money(totalAmt)}`, 40, 70);

        autoTable(doc, {
            startY: 84,
            head: [['Date', 'Category', 'Plate No.', 'Vendor', 'Amount', 'VAT', 'Total', 'Payment', 'Account', 'Paid By']],
            body,
            foot: [['', '', '', 'TOTAL', money(totalNet), money(totalVat), money(totalAmt), '', '', '']],
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 5, textColor: [30, 41, 59] },
            headStyles: { fillColor: [22, 83, 156], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: { 4: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
            didDrawPage: () => {
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - 70, doc.internal.pageSize.getHeight() - 20);
            }
        });

        doc.save(`expenses_report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getLast12Months = () => {
        const months = [];
        const date = new Date();
        for (let i = 0; i < 12; i++) {
            const m = date.getMonth();
            const y = date.getFullYear();
            months.push({
                label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                value: `${y}-${String(m + 1).padStart(2, '0')}`
            });
            date.setMonth(date.getMonth() - 1);
        }
        return months;
    };

    const getCategoryColors = (cat = '') => {
        const lower = cat.toLowerCase();
        if (lower.includes('salary') || lower.includes('payroll')) {
            return { bg: '#fef3c7', text: '#d97706', border: '#fde68a' };
        }
        if (lower.includes('insurance')) {
            return { bg: '#f3e8ff', text: '#7c3aed', border: '#e9d5ff' };
        }
        if (lower.includes('registration') || lower.includes('permit')) {
            return { bg: '#fce7f3', text: '#db2777', border: '#fbcfe8' };
        }
        if (lower.includes('maintenance') || lower.includes('repair')) {
            return { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' };
        }
        if (lower.includes('office') || lower.includes('rent') || lower.includes('purchase')) {
            return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' };
        }
        if (lower.includes('visa') || lower.includes('staff')) {
            return { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' };
        }
        if (lower.includes('setup') || lower.includes('capital') || lower.includes('cost')) {
            return { bg: '#e6f0fa', text: '#16539C', border: '#c3dcf5' };
        }
        if (lower.includes('fuel') || lower.includes('gas')) {
            return { bg: '#ffedd5', text: '#ea580c', border: '#fed7aa' };
        }
        const themes = [
            { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
            { bg: '#ecfeff', text: '#0891b2', border: '#c5f2f7' },
            { bg: '#fdf2f8', text: '#db2777', border: '#fbcfe8' },
        ];
        return themes[cat.length % themes.length];
    };

    const masterData = { categories, paymentMethods, vendors, vehicles, departments, drivers, clients, operatorId, country };

    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateExpensePage
                    editItem={editItem}
                    operatorDetails={operatorDetails}
                    masterData={masterData}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => {
                        setCreatePageOpen(false);
                        fetchExpenses();
                    }}
                />
            </div>
        );
    }

    /* ── Filter logic ── */
    const filtered = expenses.filter(exp => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const categoryMatch = exp.category?.toLowerCase().includes(term);
            const methodMatch = exp.paymentMethod?.toLowerCase().includes(term);
            const notesMatch = exp.notes?.toLowerCase().includes(term);
            const vendorMatch = exp.vendorId?.vendorName?.toLowerCase().includes(term);
            const vehicleMatch = exp.vehicleId?.plateNumber?.toLowerCase().includes(term);
            if (!categoryMatch && !methodMatch && !notesMatch && !vendorMatch && !vehicleMatch) {
                return false;
            }
        }
        if (selectedCategory && selectedCategory !== 'All') {
            if (exp.category !== selectedCategory) return false;
        }
        if (selectedMonth && selectedMonth !== 'All') {
            const [y, m] = selectedMonth.split('-');
            const expDate = new Date(exp.date);
            if (expDate.getFullYear() !== parseInt(y) || (expDate.getMonth() + 1) !== parseInt(m)) {
                return false;
            }
        }
        if (fromDate) {
            const expDate = new Date(exp.date).setHours(0, 0, 0, 0);
            const fromLimit = new Date(fromDate).setHours(0, 0, 0, 0);
            if (expDate < fromLimit) return false;
        }
        if (toDate) {
            const expDate = new Date(exp.date).setHours(23, 59, 59, 999);
            const toLimit = new Date(toDate).setHours(23, 59, 59, 999);
            if (expDate > toLimit) return false;
        }
        return true;
    });

    /* ── Sorting logic ── */
    const sorted = [...filtered].sort((a, b) => {
        let valA, valB;
        if (sortField === 'date') {
            valA = new Date(a.date).getTime();
            valB = new Date(b.date).getTime();
        } else if (sortField === 'category') {
            valA = (a.category || '').toLowerCase();
            valB = (b.category || '').toLowerCase();
        } else if (sortField === 'vendor') {
            valA = (a.vendorId?.vendorName || '').toLowerCase();
            valB = (b.vendorId?.vendorName || '').toLowerCase();
        } else if (sortField === 'amount' || sortField === 'total') {
            valA = a.amount || 0;
            valB = b.amount || 0;
        } else {
            valA = a[sortField] || '';
            valB = b[sortField] || '';
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    /* ── Pagination ── */
    const totalPages = Math.ceil(sorted.length / itemsPerPage);
    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    /* ── Calculations for premium top KPI Cards ── */
    const totalSum = filtered.reduce((s, e) => s + (e.amount || 0), 0);
    const totalVat = filtered.reduce((s, e) => {
        const isEx = e.category?.toLowerCase().includes('fuel') || e.category?.toLowerCase().includes('utility');
        return s + (isEx ? 0 : (e.amount || 0) * 0.05);
    }, 0);
    const totalNet = totalSum - totalVat;

    /* ── Category breakdown carousel data ── */
    const breakdown = Object.values(
        filtered.reduce((acc, exp) => {
            const cat = exp.category || 'General';
            if (!acc[cat]) acc[cat] = { category: cat, amount: 0, count: 0 };
            acc[cat].amount += exp.amount || 0;
            acc[cat].count += 1;
            return acc;
        }, {})
    ).sort((a, b) => b.amount - a.amount);

    return (
        <div className="operator-page">
            <ExpenseImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={fetchExpenses}
                operatorId={operatorId}
                vehicles={vehicles}
                vendors={vendors}
                onDownloadTemplate={handleDownloadTemplate}
            />

            {/* Breadcrumb */}
            <div className="op-breadcrumb">
                <HomeIcon />
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Expenses</span>
            </div>

            {/* Title & Top Action Row */}
            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Business Expenses</h1>
                    <p className="op-subtitle">Cost Tracking & Management with VAT</p>
                </div>
                <div className="op-header-actions">
                    <button className="op-btn op-btn-outline-blue" onClick={handleManageCategories}>
                        <Icon name="cog" size={15} />
                        Manage Categories
                    </button>
                    <button className="op-btn op-btn-outline-blue" onClick={handleDownloadTemplate}>
                        <Icon name="template" size={15} />
                        Template
                    </button>
                    <button className="op-btn op-btn-outline-blue" onClick={() => setImportOpen(true)}>
                        <Icon name="import" size={15} />
                        Import
                    </button>
                    <button className="op-btn op-btn-solid-primary" onClick={() => openCreate()}>
                        <Icon name="plus" size={15} />
                        Add Expense
                    </button>
                </div>
            </div>

            {/* Top KPI Blocks (4 cards) */}
            <div className="op-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 26 }}>
                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #2563eb', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Expenses</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="arrowDown" size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalSum, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>{filtered.length} records</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #1d4ed8', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>VAT Amount</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="percent" size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalVat, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>5% Standard VAT</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #0284c7', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Amount</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f0f9ff', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="wallet" size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalNet, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>Excluding VAT</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #1e40af', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Categories</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="grid" size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{categories.length || breakdown.length}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>Active categories</div>
                </div>
            </div>

            {/* Dynamic Categories Breakdown Carousel */}
            {breakdown.length > 0 && (
                <div className="op-categories-carousel">
                    {breakdown.map((item, idx) => {
                        const colors = getCategoryColors(item.category);
                        const percent = totalSum > 0 ? ((item.amount / totalSum) * 100).toFixed(1) : '0';
                        return (
                            <div key={idx} className="op-category-card" style={{ '--cat-color': colors.text }}>
                                <span className="op-category-pill" style={{ '--cat-bg': colors.bg, '--cat-text': colors.text, '--cat-border': colors.border, width: 'fit-content' }}>
                                    {item.category}
                                </span>
                                <span className="op-category-amount">{formatCurrency(item.amount, country)}</span>
                                <span className="op-category-sub">{item.count} {item.count === 1 ? 'record' : 'records'} • {percent}%</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters & Export Card */}
            <div className="op-filters-card">
                <div className="op-filters-header">
                    <div className="op-filters-title">
                        <Icon name="filter" size={15} />
                        Filters & Export
                    </div>
                    <div className="op-filters-actions">
                        <button className="op-btn op-btn-outline-blue" style={{ padding: '8px 14px' }} onClick={handleResetFilters}>
                            <Icon name="refresh" size={14} />
                            Reset
                        </button>
                        <button className="op-btn op-btn-outline-blue" style={{ padding: '8px 14px', color: '#16a34a', borderColor: '#bbf7d0' }} onClick={handleExportExcel}>
                            <Icon name="excel" size={14} />
                            Excel
                        </button>
                        <button className="op-btn op-btn-outline-red" style={{ padding: '8px 14px' }} onClick={handleDownloadPDF} title="Download PDF file">
                            <Icon name="pdf" size={14} />
                            PDF
                        </button>
                        <button className="op-btn op-btn-outline-blue" style={{ padding: '8px 14px', color: '#475569', borderColor: '#cbd5e1' }} onClick={handlePrintPDF} title="Print this page">
                            <Icon name="printer" size={14} />
                            Print
                        </button>
                    </div>
                </div>

                <div className="op-filters-grid">
                    <div className="op-filter-field">
                        <label>Search</label>
                        <div className="op-filter-input-wrapper">
                            <span className="op-filter-icon"><Icon name="search" size={14} /></span>
                            <input
                                type="text"
                                className="op-filter-input"
                                placeholder="Vendor, category, notes..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>

                    <div className="op-filter-field">
                        <label>Category</label>
                        <select
                            className="op-filter-select"
                            value={selectedCategory}
                            onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">All Categories</option>
                            {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>

                    <div className="op-filter-field">
                        <label>Month</label>
                        <select
                            className="op-filter-select"
                            value={selectedMonth}
                            onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="All">All Months</option>
                            {getLast12Months().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>

                    <div className="op-filter-field">
                        <label>From Date</label>
                        <input
                            type="date"
                            className="op-filter-date"
                            value={fromDate}
                            onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    <div className="op-filter-field">
                        <label>To Date</label>
                        <input
                            type="date"
                            className="op-filter-date"
                            value={toDate}
                            onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>
            </div>

            {/* Expense Records Section */}
            <div className="op-records-card">
                <div className="op-records-header">
                    <h2 className="op-records-title">Expense Records ({sorted.length} of {expenses.length} records)</h2>
                </div>

                <div className="op-table-wrapper">
                    <table className="op-records-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                    Date {sortField === 'date' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                                </th>
                                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                    Category {sortField === 'category' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                                </th>
                                <th>Plate No.</th>
                                <th onClick={() => handleSort('vendor')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                    Vendor {sortField === 'vendor' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                                </th>
                                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                    Amount {sortField === 'amount' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                                </th>
                                <th>VAT</th>
                                <th onClick={() => handleSort('total')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                    Total {sortField === 'total' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                                </th>
                                <th>Payment</th>
                                <th>Account</th>
                                <th>Paid By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="11" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                                        Loading expenses…
                                    </td>
                                </tr>
                            ) : paginated.length === 0 ? (
                                <tr>
                                    <td colSpan="11" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                                        {searchTerm || selectedCategory || selectedMonth !== 'All' || fromDate || toDate
                                            ? 'No expenses match your search'
                                            : 'No expenses recorded yet — click "Add Expense" to start'}
                                    </td>
                                </tr>
                            ) : paginated.map(exp => {
                                const isEx = exp.category?.toLowerCase().includes('fuel') || exp.category?.toLowerCase().includes('utility');
                                const netAmt = isEx ? exp.amount : exp.amount * 0.95;
                                const vatAmt = isEx ? 0 : exp.amount * 0.05;
                                const colors = getCategoryColors(exp.category);

                                return (
                                    <tr key={exp._id}>
                                        {/* Date */}
                                        <td style={{ fontWeight: 600, color: '#374151' }}>
                                            {new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </td>

                                        {/* Category */}
                                        <td>
                                            <span className="op-category-pill" style={{ '--cat-bg': colors.bg, '--cat-text': colors.text, '--cat-border': colors.border }}>
                                                {exp.category}
                                            </span>
                                        </td>

                                        {/* Plate No */}
                                        <td>
                                            {exp.vehicleId?.plateNumber ? (
                                                <div className="op-plate-badge">
                                                    <span className="op-plate-number">{exp.vehicleId.plateNumber}</span>
                                                    <span className="op-vehicle-model">
                                                        {vehicles.find(v => v._id === exp.vehicleId?._id)?.model || 'Coaster'}
                                                    </span>
                                                </div>
                                            ) : '—'}
                                        </td>

                                        {/* Vendor */}
                                        <td style={{ fontWeight: 500, color: '#1e293b' }}>
                                            {exp.vendorId?.vendorName || '—'}
                                        </td>

                                        {/* Net Amount */}
                                        <td style={{ fontWeight: 600, color: '#475569' }}>
                                            {formatCurrency(netAmt, country)}
                                        </td>

                                        {/* VAT */}
                                        <td>
                                            {isEx ? (
                                                <span className="op-vat-badge" style={{ background: '#f8fafc', color: '#64748b', borderColor: '#cbd5e1' }}>Exempt</span>
                                            ) : (
                                                <span className="op-vat-badge" style={{ background: '#f1f5f9', color: '#1e293b', borderColor: '#94a3b8' }}>5% VAT</span>
                                            )}
                                        </td>

                                        {/* Total */}
                                        <td>
                                            <span className="op-total-value">{formatCurrency(exp.amount, country)}</span>
                                        </td>

                                        {/* Payment */}
                                        <td>
                                            <span className="op-payment-badge">{exp.paymentMethod}</span>
                                        </td>

                                        {/* Account Info */}
                                        <td>
                                            <div className="op-account-info">
                                                {exp.paymentMethod?.toLowerCase() === 'cash' ? (
                                                    <>
                                                        <span className="op-account-badge">Petty Cash</span>
                                                        <span className="op-account-sub">Office Petty Cash</span>
                                                        <span className="op-account-sub" style={{ fontSize: '10px' }}>
                                                            Handler: {exp.driverId?.name || exp.notes?.split(' ')[0] || 'Mazid SK'}
                                                        </span>
                                                    </>
                                                ) : exp.paymentMethod?.toLowerCase() === 'bank' || exp.paymentMethod?.toLowerCase() === 'online' ? (
                                                    <>
                                                        <span className="op-account-badge" style={{ background: '#eff6ff', color: '#2563eb', borderColor: '#bfdbfe' }}>Bank Account</span>
                                                        <span className="op-account-sub">Corporate Account</span>
                                                        <span className="op-account-sub" style={{ fontSize: '10px' }}>
                                                            Paid: {exp.driverId?.name || 'Company'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="op-account-badge" style={{ background: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' }}>General</span>
                                                        <span className="op-account-sub">Company Outflow</span>
                                                    </>
                                                )}
                                            </div>
                                        </td>

                                        {/* Paid By / Attachment */}
                                        <td>
                                            {exp.attachment ? (
                                                <a href={`${UPLOAD_URL}${exp.attachment}`} target="_blank" rel="noreferrer"
                                                    style={{ color: '#10b981', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700 }}>
                                                    <Icon name="eye" size={13} /> View
                                                </a>
                                            ) : (
                                                <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="inv-actions">
                                            <button className="op-action-btn" title="Edit" onClick={() => openCreate(exp)}>
                                                <Icon name="edit" size={13} />
                                            </button>
                                            <button className="op-action-btn btn-delete" title="Delete" onClick={() => handleDelete(exp._id)}>
                                                <Icon name="trash" size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="op-pagination">
                        <div className="page-info" style={{ fontStyle: 'normal', color: '#64748b', fontWeight: 500 }}>
                            Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, sorted.length)} of {sorted.length} records
                        </div>
                        <div className="page-controls" style={{ display: 'flex', gap: '8px' }}>
                            <button
                                className="page-btn op-btn op-btn-outline-blue"
                                style={{ padding: '6px 12px', fontSize: '12px' }}
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    className={`page-btn op-btn ${currentPage === i + 1 ? 'op-btn-solid-red' : 'op-btn-outline-blue'}`}
                                    style={{ padding: '6px 12px', fontSize: '12px', minWidth: '32px' }}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                className="page-btn op-btn op-btn-outline-blue"
                                style={{ padding: '6px 12px', fontSize: '12px' }}
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
